import { partition } from 'lodash-es';

import { isBelowThreshold } from './is-below-threshold.js';
import type { Formatter } from '../../core/types/formatter.js';
import { ClusterNode } from '../types/guards/node-guards.js';
import type { NameAccessor } from '../types/treemap.js';
import { getAggregatedLeaf } from '../utils/node-aggregations.js';

/**
 * Calculates the aggregated data for a treemap based on the provided data and treemap area.
 * @param cluster - TreeMap data cluster node
 * @param areaPerValue - proportionate area of treemap per value
 * @param minNodeSize - node threshold in pixels
 * @param nameAccessor - leaf object property (name or value)
 * @returns - TreeMap data child node with aggregated nodes
 */

export const buildAggregatedLeaf = (
  cluster: ClusterNode,
  areaPerValue: number,
  minNodeSize: number,
  formatter: Formatter,
  nameAccessor?: NameAccessor,
): ClusterNode => {
  if (cluster.children.length === 1) {
    return cluster;
  }

  const [nodesToAggregate, remainingNodes] = partition(
    cluster.children,
    (node) => isBelowThreshold(node, areaPerValue, minNodeSize),
  );

  const singleChildCluster = cluster.children.length === 1;

  if (!nodesToAggregate.length || singleChildCluster) {
    return cluster;
  }

  const clusterName = cluster.name;
  const clusterColorToken = cluster.color;

  const aggregatedNode = getAggregatedLeaf(
    nodesToAggregate,
    clusterName,
    formatter,
    clusterColorToken,
    nameAccessor,
  );

  // if aggregatedNode is still below threshold, merge it to the smallest remainingNode
  if (isBelowThreshold(aggregatedNode, areaPerValue, minNodeSize)) {
    const smallestRemainingNode = remainingNodes.pop() ?? {
      name: '',
      value: 0,
      ...(nameAccessor && { [nameAccessor]: '' }),
    };
    aggregatedNode.value += smallestRemainingNode.value;
    // TODO check that this code is not running when nameAccessor = value
    nameAccessor &&
      aggregatedNode.aggregatedNodes?.push(
        smallestRemainingNode[nameAccessor] as string,
      );
  }
  return { ...cluster, children: [...remainingNodes, aggregatedNode] };
};
